import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import ResourceCard from 'app/components/Resources/ResourceCard';
import { Sparkle } from 'app/components/shared/icons';
import { AnimatePresence, useAnimate } from 'framer-motion';
import { isNLPLoadingSelector } from 'store/reducers/nlp/nlp-reducer';
import { setShowingLoadingScreen } from 'store/reducers/resources-steps-reducer/resources-steps-reducer';
import {
  isResourcesLoadingSelector,
  previousFiltersLengthSelector,
} from 'store/reducers/resources/resources-reducer';

import {
  ResourcesStepsOneLoadingCardsContainer,
  ResourcesStepsOneLoadingContainer,
  ResourcesStepsOneLoadingGif,
  ResourcesStepsOneLoadingTitle,
  ResourcesStepsOneLoadingTitleContainer,
  ResourcesStepsOneLoadingWrapper,
} from './styled';

export default function ResourcesStepsOneLoading() {
  const [scope, animate] = useAnimate();
  const dispatch = useDispatch();
  const isNlpLoading = useSelector(isNLPLoadingSelector);
  const filters = useSelector(previousFiltersLengthSelector);
  const isResourcesLoading = useSelector(isResourcesLoadingSelector);

  async function animateLoading() {
    await animate(scope.current, { opacity: [0, 1] }, { duration: 0.2 });
    await animate(scope.current, { top: 0, bottom: -500 }, { duration: 0.5 });
    await animate(scope.current, { opacity: [1, 0] }, { delay: 1.8 });
  }

  const isLoading = useMemo<boolean>(() => {
    return isNlpLoading || (isResourcesLoading && filters > 0);
  }, [isNlpLoading, isResourcesLoading, filters]);

  useEffect(() => {
    if (!isLoading) {
      animateLoading();
    }
  }, [isLoading]);

  useEffect(() => {
    dispatch(setShowingLoadingScreen(true));
  }, []);

  useEffect(() => {
    if (!isLoading) {
      const timeout = setTimeout(() => {
        dispatch(setShowingLoadingScreen(false));
      }, 2000);

      return () => clearTimeout(timeout);
    }
  }, [isLoading]);

  return (
    <ResourcesStepsOneLoadingWrapper>
      <AnimatePresence>{isLoading ? <ResourcesStepsOneLoadingGif /> : null}</AnimatePresence>
      <AnimatePresence>
        {!isLoading ? (
          <ResourcesStepsOneLoadingContainer ref={scope}>
            <ResourcesStepsOneLoadingTitleContainer>
              <Sparkle width={24} height={24} fill="var(--color-primary)" />
              <ResourcesStepsOneLoadingTitle>
                Preparing the results...
              </ResourcesStepsOneLoadingTitle>
            </ResourcesStepsOneLoadingTitleContainer>
            <ResourcesStepsOneLoadingCardsContainer>
              {Array.from({ length: 3 }).map((_, index) => (
                <ResourceCard.Skeleton key={index} />
              ))}
            </ResourcesStepsOneLoadingCardsContainer>
          </ResourcesStepsOneLoadingContainer>
        ) : null}
      </AnimatePresence>
    </ResourcesStepsOneLoadingWrapper>
  );
}
